import {
  CollectionRevisionCurrentDto,
  CollectionRevisionTargetFileDto,
  CollectionRevisionValidationFileDto,
  CollectionValidationRequiredInput,
  SOURCE_STATEMENT_CURRENCY_INPUT_KEY,
  STATEMENT_ORIGIN_INPUT_KEY,
  StatementOriginRequiredInput,
  StatementOriginRequiredInputConstraint,
  VersionedCollectionRevisionTargetFileSet,
} from '../interfaces/collection-revision.interface';
import { isStatementOriginValidationErrorCode } from '../interfaces/statement-origin.interface';

const CANONICAL_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATEMENT_ORIGIN_CONSTRAINTS: ReadonlySet<string> = new Set([
  'ALL_VISIBLE',
  'BRAZIL_PRO',
]);

/**
 * A required-input entry the state machine could not resolve to a known,
 * well-formed shape: either the key itself is not one this frontend
 * understands (`UNKNOWN_KEY`) or the key is recognised but the remaining
 * shape does not match the wire contract exactly (`MALFORMED`). Both cases
 * are treated as fail-closed: the review UI shows the generic "unsupported
 * input" alert and submission stays blocked.
 */
export interface UnsupportedRequiredInputDescriptor {
  readonly key: string;
  readonly supported: false;
  readonly reason: 'UNKNOWN_KEY' | 'MALFORMED';
}

export type CollectionRequiredInputDescriptor =
  | CollectionValidationRequiredInput
  | UnsupportedRequiredInputDescriptor;

/**
 * Parses one raw `required_inputs` wire entry into a closed descriptor.
 * Fail-closed: a non-record entry or a missing/empty `key` throws (today's
 * `requireNonEmpty` semantics); a recognised key whose remaining shape does
 * not match the wire contract exactly becomes `{ supported: false, reason:
 * 'MALFORMED' }`; any other key becomes `{ supported: false, reason:
 * 'UNKNOWN_KEY' }`. Supported descriptors are returned frozen with exactly
 * the wire keys — no synthetic fields are ever added.
 */
export function parseCollectionRequiredInput(
  raw: unknown,
): CollectionRequiredInputDescriptor {
  if (!isRecord(raw) || !isNonEmptyString(raw['key'])) {
    throw new TypeError('required input key must be a non-empty string');
  }
  const key = raw['key'];
  const keys = Object.keys(raw);

  if (key === SOURCE_STATEMENT_CURRENCY_INPUT_KEY) {
    if (keys.length !== 1) {
      return unsupportedDescriptor(key, 'MALFORMED');
    }
    return Object.freeze({ key: SOURCE_STATEMENT_CURRENCY_INPUT_KEY });
  }

  if (key === STATEMENT_ORIGIN_INPUT_KEY) {
    const expectedKeys = new Set([
      'key',
      'catalog',
      'constraint',
      'allow_create',
    ]);
    if (
      keys.length !== expectedKeys.size ||
      !keys.every((candidate) => expectedKeys.has(candidate)) ||
      raw['catalog'] !== 'STATEMENT_ORIGINS' ||
      !isStatementOriginConstraint(raw['constraint']) ||
      typeof raw['allow_create'] !== 'boolean'
    ) {
      return unsupportedDescriptor(key, 'MALFORMED');
    }
    return Object.freeze({
      key: STATEMENT_ORIGIN_INPUT_KEY,
      catalog: 'STATEMENT_ORIGINS' as const,
      constraint: raw['constraint'] as StatementOriginRequiredInputConstraint,
      allow_create: raw['allow_create'],
    });
  }

  return unsupportedDescriptor(key, 'UNKNOWN_KEY');
}

/** True for a descriptor that is one of the closed, wire-supported shapes. */
export function isSupportedRequiredInputDescriptor(
  descriptor: CollectionRequiredInputDescriptor,
): descriptor is CollectionValidationRequiredInput {
  return !('supported' in descriptor);
}

/** True for a supported descriptor whose key is `statement_origin_id`. */
export function isStatementOriginRequiredInput(
  descriptor: CollectionRequiredInputDescriptor,
): descriptor is StatementOriginRequiredInput {
  return (
    isSupportedRequiredInputDescriptor(descriptor) &&
    descriptor.key === STATEMENT_ORIGIN_INPUT_KEY
  );
}

function unsupportedDescriptor(
  key: string,
  reason: 'UNKNOWN_KEY' | 'MALFORMED',
): UnsupportedRequiredInputDescriptor {
  return Object.freeze({ key, supported: false as const, reason });
}

function isStatementOriginConstraint(
  value: unknown,
): value is StatementOriginRequiredInputConstraint {
  return typeof value === 'string' && STATEMENT_ORIGIN_CONSTRAINTS.has(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export type CollectionRevisionFileEditorPhase =
  | 'EMPTY'
  | 'READY'
  | 'UPLOADING'
  | 'VALIDATING'
  | 'UPDATING'
  | 'RELOADING'
  | 'CONFLICT'
  | 'ERROR';

export type CollectionRevisionFileEditorBusyPhase = Extract<
  CollectionRevisionFileEditorPhase,
  'UPLOADING' | 'VALIDATING' | 'UPDATING' | 'RELOADING'
>;

export type CollectionRevisionFileSetMode =
  | 'INITIAL_SUBMISSION'
  | 'EXISTING_FILE_SET';

export type CollectionRevisionNavigationDirection = 'BACK' | 'NEXT';
export type CollectionRevisionNavigationIntent =
  | CollectionRevisionNavigationDirection
  | 'EXTERNAL';

export interface CollectionRevisionPersistedFileRow {
  readonly key: string;
  readonly origin: 'PERSISTED';
  readonly fileId: string;
  readonly name: string | null;
  readonly mediaType: string | null;
  readonly sizeBytes: number | null;
  readonly validationStatus: CollectionRevisionTargetFileDto['validation_status'];
}

export interface CollectionRevisionLocalFileRow {
  readonly key: string;
  readonly origin: 'LOCAL';
  readonly clientId: string;
  readonly file: File;
  readonly name: string;
  readonly mediaType: string | null;
  readonly sizeBytes: number;
  readonly validationStatus: 'UNKNOWN';
}

export type CollectionRevisionFileEditorRow =
  | CollectionRevisionPersistedFileRow
  | CollectionRevisionLocalFileRow;

export interface CollectionRevisionLocalFileCandidate {
  readonly clientId: string;
  readonly file: File;
}

export type CollectionRevisionLocalUploadState =
  | 'SELECTED'
  | 'UPLOADING'
  | 'STAGED'
  | 'UPLOAD_FAILED';

export interface CollectionRevisionFileEditorDirtySummary {
  readonly added: number;
  readonly removed: number;
  readonly addedFileNames: readonly string[];
  readonly removedFileNames: readonly string[];
}

export type CollectionRevisionFileEditorFailureKind =
  | 'UPLOAD'
  | 'VALIDATION'
  | 'COMMIT'
  | 'SESSION';

export interface CollectionRevisionFileEditorFailure {
  readonly kind: CollectionRevisionFileEditorFailureKind;
  readonly message: string;
  readonly fileNames: readonly string[];
  readonly cause: unknown;
}

/**
 * The frozen per-file validation projection held by the editor. Identical to
 * the wire `CollectionRevisionValidationFileDto` except `required_inputs`,
 * which is parsed through `parseCollectionRequiredInput` (so unknown or
 * malformed entries surface as `UnsupportedRequiredInputDescriptor` instead
 * of being trusted blindly) and — per the statement-origin rejection policy
 * — may hold a previous file's retained origin descriptor instead of the
 * empty array a rejection response carries.
 */
export type CollectionRevisionFileValidationSnapshot = Omit<
  CollectionRevisionValidationFileDto,
  'required_inputs'
> & {
  readonly required_inputs: readonly CollectionRequiredInputDescriptor[];
};

export interface CollectionRevisionFileEditorSnapshot {
  readonly phase: CollectionRevisionFileEditorPhase;
  readonly busy: boolean;
  readonly mode: CollectionRevisionFileSetMode;
  readonly editable: boolean;
  readonly requiredFilesCount: number;
  readonly rows: readonly CollectionRevisionFileEditorRow[];
  readonly requiredInputsByFile: Readonly<
    Record<string, readonly CollectionRequiredInputDescriptor[]>
  >;
  readonly resolvedInputsByFile: Readonly<
    Record<string, Readonly<Record<string, string>>>
  >;
  readonly validationByFile: Readonly<
    Record<string, CollectionRevisionFileValidationSnapshot>
  >;
  readonly uploadStateByClientId: Readonly<
    Record<string, CollectionRevisionLocalUploadState>
  >;
  readonly baseFileIds: readonly string[];
  readonly targetEtag: string | null;
  readonly revisionEtag: string | null;
  readonly dirty: boolean;
  readonly dirtySummary: CollectionRevisionFileEditorDirtySummary;
  readonly canCommitDesiredFileSet: boolean;
  readonly primaryActionEnabled: boolean;
  readonly pendingDecision: CollectionRevisionPendingDecision | null;
  readonly conflict: CollectionRevisionFileEditorConflict | null;
  readonly stagedFileIds: readonly string[];
  readonly failure: CollectionRevisionFileEditorFailure | null;
}

export interface CollectionRevisionFileEditorConflict {
  readonly current: CollectionRevisionCurrentDto;
  readonly targetEtag: string;
  readonly revisionEtag: string;
  readonly traceId: string | null;
  readonly editable: boolean;
}

export type CollectionRevisionNavigationDecision =
  | {
      readonly kind: 'NAVIGATE';
      readonly direction: CollectionRevisionNavigationIntent;
    }
  | {
      readonly kind: 'CONFIRM_UPDATE' | 'CONFIRM_DISCARD_SELECTION';
      readonly direction: CollectionRevisionNavigationIntent;
      readonly summary: CollectionRevisionFileEditorDirtySummary;
    }
  | { readonly kind: 'BLOCKED' };

export type CollectionRevisionPendingDecision = Extract<
  CollectionRevisionNavigationDecision,
  { readonly kind: 'CONFIRM_UPDATE' | 'CONFIRM_DISCARD_SELECTION' }
>;

export type CollectionRevisionPrimaryActionDecision =
  | {
      readonly kind: 'SUBMIT_INITIAL';
      readonly direction: 'NEXT';
    }
  | {
      readonly kind: 'NAVIGATE';
      readonly direction: 'NEXT';
    }
  | Extract<
      CollectionRevisionNavigationDecision,
      { readonly kind: 'CONFIRM_UPDATE' }
    >
  | { readonly kind: 'BLOCKED' };

export interface FrozenCollectionRevisionFileIntent {
  readonly direction: CollectionRevisionNavigationIntent;
  readonly targetEtag: string;
  readonly persistedFileIds: readonly string[];
  readonly localFiles: readonly CollectionRevisionLocalFileCandidate[];
}

const BUSY_PHASES: ReadonlySet<CollectionRevisionFileEditorPhase> = new Set([
  'UPLOADING',
  'VALIDATING',
  'UPDATING',
  'RELOADING',
]);

const COMMIT_PHASE_TRANSITIONS: Readonly<
  Record<
    CollectionRevisionFileEditorBusyPhase,
    CollectionRevisionFileEditorBusyPhase | null
  >
> = {
  UPLOADING: 'VALIDATING',
  VALIDATING: 'UPDATING',
  UPDATING: 'RELOADING',
  RELOADING: null,
};

export class CollectionRevisionFileEditorStateMachine {
  private phase: CollectionRevisionFileEditorPhase = 'EMPTY';
  private mode: CollectionRevisionFileSetMode = 'INITIAL_SUBMISSION';
  private editable = false;
  private requiredFilesCount = 0;
  private rows: readonly CollectionRevisionFileEditorRow[] = [];
  private requiredInputsByFile: Readonly<
    Record<string, readonly CollectionRequiredInputDescriptor[]>
  > = Object.freeze({});
  private resolvedInputsByFile: Readonly<
    Record<string, Readonly<Record<string, string>>>
  > = Object.freeze({});
  private validationByFile: Readonly<
    Record<string, CollectionRevisionFileValidationSnapshot>
  > = Object.freeze({});
  private uploadStateByClientId: Readonly<
    Record<string, CollectionRevisionLocalUploadState>
  > = Object.freeze({});
  private baseRows: readonly CollectionRevisionPersistedFileRow[] = [];
  private baseFileIds: readonly string[] = [];
  private targetEtag: string | null = null;
  private revisionEtag: string | null = null;
  private pendingDecision: CollectionRevisionPendingDecision | null = null;
  private conflict: CollectionRevisionFileEditorConflict | null = null;
  private stagedFileIds: readonly string[] = [];
  private failure: CollectionRevisionFileEditorFailure | null = null;

  snapshot(): CollectionRevisionFileEditorSnapshot {
    const summary = this.dirtySummary();
    return Object.freeze({
      phase: this.phase,
      busy: this.isBusy(),
      mode: this.mode,
      editable: this.editable,
      requiredFilesCount: this.requiredFilesCount,
      rows: Object.freeze([...this.rows]),
      requiredInputsByFile: this.requiredInputsByFile,
      resolvedInputsByFile: this.resolvedInputsByFile,
      validationByFile: this.validationByFile,
      uploadStateByClientId: this.uploadStateByClientId,
      baseFileIds: Object.freeze([...this.baseFileIds]),
      targetEtag: this.targetEtag,
      revisionEtag: this.revisionEtag,
      dirty: summary.added > 0 || summary.removed > 0,
      dirtySummary: summary,
      canCommitDesiredFileSet: this.canCommitDesiredFileSet(),
      primaryActionEnabled: this.isPrimaryActionEnabled(summary),
      pendingDecision: this.pendingDecision,
      conflict: this.conflict,
      stagedFileIds: Object.freeze([...this.stagedFileIds]),
      failure: this.failure,
    });
  }

  hydrate(source: VersionedCollectionRevisionTargetFileSet): void {
    if (this.isBusy()) {
      throw new Error(
        'Cannot hydrate the file editor during an active commit.',
      );
    }

    this.applyHydration(source, true);
  }

  completeCommit(source: VersionedCollectionRevisionTargetFileSet): void {
    if (this.phase !== 'RELOADING') {
      throw new Error('A file-set commit is not awaiting server rehydration.');
    }
    this.applyHydration(source, false);
  }

  private applyHydration(
    source: VersionedCollectionRevisionTargetFileSet,
    navigationReady: boolean,
  ): void {
    const persistedRows = [...source.targetFileSet.files]
      .sort((left, right) => left.ordinal - right.ordinal)
      .map(toPersistedRow);

    this.baseRows = Object.freeze(persistedRows);
    this.rows = Object.freeze([...persistedRows]);
    this.baseFileIds = Object.freeze(persistedRows.map((row) => row.fileId));
    this.requiredInputsByFile = Object.freeze({});
    this.resolvedInputsByFile = Object.freeze({});
    this.validationByFile = Object.freeze({});
    this.uploadStateByClientId = Object.freeze({});
    this.mode =
      source.targetFileSet.target_status === 'PENDING' &&
      persistedRows.length === 0
        ? 'INITIAL_SUBMISSION'
        : 'EXISTING_FILE_SET';
    this.requiredFilesCount = source.targetFileSet.required_files_count;
    this.targetEtag = requireValidator(source.targetEtag, 'targetEtag');
    this.revisionEtag = requireValidator(source.revisionEtag, 'revisionEtag');
    this.editable =
      (source.targetFileSet.revision_status === 'DRAFT' ||
        source.targetFileSet.revision_status === 'SUBMITTED') &&
      source.targetFileSet.editable &&
      source.targetFileSet.allowed_actions.includes('COMMIT_FILE_SET');
    if (navigationReady) {
      this.pendingDecision = null;
    }
    this.conflict = null;
    this.stagedFileIds = [];
    this.failure = null;
    this.phase = navigationReady ? 'READY' : 'RELOADING';
  }

  add(candidate: CollectionRevisionLocalFileCandidate): boolean {
    if (!this.canMutate()) return false;
    const clientId = requireNonEmpty(candidate.clientId, 'clientId');
    if (!(candidate.file instanceof File)) {
      throw new TypeError('file must be a File');
    }

    const key = `local:${clientId}`;
    const duplicate = this.rows.some(
      (row) =>
        row.key === key ||
        (row.name?.toLocaleLowerCase() ===
          candidate.file.name.toLocaleLowerCase() &&
          row.sizeBytes === candidate.file.size),
    );
    if (duplicate) return false;

    this.rows = Object.freeze([
      ...this.rows,
      Object.freeze({
        key,
        origin: 'LOCAL' as const,
        clientId,
        file: candidate.file,
        name: candidate.file.name,
        mediaType: candidate.file.type || null,
        sizeBytes: candidate.file.size,
        validationStatus: 'UNKNOWN' as const,
      }),
    ]);
    this.uploadStateByClientId = Object.freeze({
      ...this.uploadStateByClientId,
      [clientId]: 'SELECTED',
    });
    return true;
  }

  remove(key: string): boolean {
    if (!this.canMutate()) return false;
    const nextRows = this.rows.filter((row) => row.key !== key);
    if (nextRows.length === this.rows.length) return false;
    const removed = this.rows.find((row) => row.key === key);
    this.rows = Object.freeze(nextRows);
    if (removed?.origin === 'LOCAL') {
      this.uploadStateByClientId = withoutKeys(
        this.uploadStateByClientId,
        new Set([removed.clientId]),
      );
    }
    if (removed?.origin === 'PERSISTED') {
      this.clearValidationFiles([removed.fileId]);
    }
    return true;
  }

  setLocalUploadState(
    clientId: string,
    state: CollectionRevisionLocalUploadState,
  ): void {
    const exactClientId = requireNonEmpty(clientId, 'clientId');
    if (
      !this.rows.some(
        (row) => row.origin === 'LOCAL' && row.clientId === exactClientId,
      )
    ) {
      throw new Error('Upload state requires an active local file row.');
    }
    this.uploadStateByClientId = Object.freeze({
      ...this.uploadStateByClientId,
      [exactClientId]: state,
    });
  }

  recordValidation(
    files: readonly CollectionRevisionValidationFileDto[],
  ): void {
    const validationByFile: Record<
      string,
      CollectionRevisionFileValidationSnapshot
    > = {};
    const requiredInputsByFile: Record<
      string,
      readonly CollectionRequiredInputDescriptor[]
    > = {};
    const resolvedInputsByFile: Record<
      string,
      Readonly<Record<string, string>>
    > = {};
    for (const file of files) {
      const fileId = requireNonEmpty(file.file_id, 'file_id');
      const frozen = freezeValidation(file, this.requiredInputsByFile[fileId]);
      validationByFile[fileId] = frozen;
      requiredInputsByFile[fileId] = frozen.required_inputs;
      resolvedInputsByFile[fileId] = frozen.resolved_inputs;
    }
    this.validationByFile = Object.freeze(validationByFile);
    this.requiredInputsByFile = Object.freeze(requiredInputsByFile);
    this.resolvedInputsByFile = Object.freeze(resolvedInputsByFile);
  }

  recordFileValidation(file: CollectionRevisionValidationFileDto): void {
    const fileId = requireNonEmpty(file.file_id, 'file_id');
    const frozen = freezeValidation(file, this.requiredInputsByFile[fileId]);
    this.validationByFile = Object.freeze({
      ...this.validationByFile,
      [fileId]: frozen,
    });
    this.requiredInputsByFile = Object.freeze({
      ...this.requiredInputsByFile,
      [fileId]: frozen.required_inputs,
    });
    this.resolvedInputsByFile = Object.freeze({
      ...this.resolvedInputsByFile,
      [fileId]: frozen.resolved_inputs,
    });
  }

  resolveRequiredInput(fileId: string, key: string, value: string): boolean {
    const exactFileId = requireNonEmpty(fileId, 'fileId');
    const exactKey = requireNonEmpty(key, 'required input key');
    if (exactKey === SOURCE_STATEMENT_CURRENCY_INPUT_KEY) {
      return this.resolveSourceStatementCurrency(exactFileId, exactKey, value);
    }
    if (exactKey === STATEMENT_ORIGIN_INPUT_KEY) {
      return this.resolveStatementOriginId(exactFileId, exactKey, value);
    }
    return false;
  }

  private resolveSourceStatementCurrency(
    fileId: string,
    key: string,
    value: string,
  ): boolean {
    const requirements = this.requiredInputsByFile[fileId] ?? [];
    if (
      !requirements.some(
        (input) =>
          isSupportedRequiredInputDescriptor(input) && input.key === key,
      )
    ) {
      return false;
    }
    const normalizedValue = requireNonEmpty(
      value,
      'required input value',
    ).toUpperCase();
    this.setResolvedInput(fileId, key, normalizedValue);
    return true;
  }

  private resolveStatementOriginId(
    fileId: string,
    key: string,
    value: string,
  ): boolean {
    const requirements = this.requiredInputsByFile[fileId] ?? [];
    if (!requirements.some((input) => isStatementOriginRequiredInput(input))) {
      return false;
    }
    // D-07: stored EXACTLY as given — no trim-normalisation, no
    // upper-casing, no rewriting — and only when it already matches the
    // canonical UUID shape (a padded or malformed value simply fails the
    // fully-anchored pattern below and is rejected, never coerced).
    if (typeof value !== 'string' || value.length === 0) return false;
    if (!CANONICAL_UUID_PATTERN.test(value)) return false;
    this.setResolvedInput(fileId, key, value);
    return true;
  }

  private setResolvedInput(fileId: string, key: string, value: string): void {
    this.resolvedInputsByFile = Object.freeze({
      ...this.resolvedInputsByFile,
      [fileId]: freezeResolvedInputs({
        ...(this.resolvedInputsByFile[fileId] ?? {}),
        [key]: value,
      }),
    });
  }

  resolvedInputsFor(fileId: string): Readonly<Record<string, string>> {
    return (
      this.resolvedInputsByFile[requireNonEmpty(fileId, 'fileId')] ??
      Object.freeze({})
    );
  }

  clearValidationFiles(fileIds: readonly string[]): void {
    const removed = new Set(
      fileIds.map((fileId) => requireNonEmpty(fileId, 'fileId')),
    );
    this.requiredInputsByFile = withoutKeys(this.requiredInputsByFile, removed);
    this.resolvedInputsByFile = withoutKeys(this.resolvedInputsByFile, removed);
    this.validationByFile = withoutKeys(this.validationByFile, removed);
  }

  requestNavigation(
    direction: CollectionRevisionNavigationIntent,
  ): CollectionRevisionNavigationDecision {
    if (this.isBusy()) return { kind: 'BLOCKED' };
    if (!this.snapshot().dirty) return { kind: 'NAVIGATE', direction };

    const decision: CollectionRevisionPendingDecision = {
      kind:
        this.mode === 'INITIAL_SUBMISSION'
          ? 'CONFIRM_DISCARD_SELECTION'
          : 'CONFIRM_UPDATE',
      direction,
      summary: this.dirtySummary(),
    };
    this.pendingDecision = Object.freeze(decision);
    return decision;
  }

  requestPrimaryAction(): CollectionRevisionPrimaryActionDecision {
    if (this.isBusy()) return { kind: 'BLOCKED' };

    const dirty = this.snapshot().dirty;
    if (this.mode === 'INITIAL_SUBMISSION') {
      if (!this.editable || !dirty || !this.canCommitDesiredFileSet()) {
        return { kind: 'BLOCKED' };
      }
      return { kind: 'SUBMIT_INITIAL', direction: 'NEXT' };
    }

    if (!dirty) return { kind: 'NAVIGATE', direction: 'NEXT' };
    if (!this.editable || !this.canCommitDesiredFileSet()) {
      return { kind: 'BLOCKED' };
    }
    return this.requestNavigation('NEXT') as Extract<
      CollectionRevisionPrimaryActionDecision,
      { readonly kind: 'CONFIRM_UPDATE' }
    >;
  }

  stay(): boolean {
    if (this.isBusy() || this.pendingDecision === null) return false;
    this.pendingDecision = null;
    return true;
  }

  discardPendingDecision(): CollectionRevisionNavigationDecision {
    if (this.isBusy() || this.pendingDecision === null) {
      return { kind: 'BLOCKED' };
    }

    const direction = this.pendingDecision.direction;
    this.rows = Object.freeze([...this.baseRows]);
    this.pendingDecision = null;
    this.phase = 'READY';
    return { kind: 'NAVIGATE', direction };
  }

  beginCommit(
    direction: CollectionRevisionNavigationIntent,
  ): FrozenCollectionRevisionFileIntent {
    if (!this.editable) {
      throw new Error('Cannot commit a read-only file editor.');
    }
    if (this.isBusy()) {
      throw new Error('A file-set commit is already active.');
    }
    if (!this.snapshot().dirty) {
      throw new Error('Cannot commit a clean file editor.');
    }
    if (!this.canCommitDesiredFileSet()) {
      throw new Error('Cannot commit below the required file count.');
    }
    if (!this.targetEtag) {
      throw new Error('Cannot commit before the target file set is hydrated.');
    }

    this.pendingDecision = null;
    this.conflict = null;
    this.failure = null;
    this.phase = 'UPLOADING';
    return Object.freeze({
      direction,
      targetEtag: this.targetEtag,
      persistedFileIds: Object.freeze(
        this.rows.flatMap((row) =>
          row.origin === 'PERSISTED' ? [row.fileId] : [],
        ),
      ),
      localFiles: Object.freeze(
        this.rows.flatMap((row) =>
          row.origin === 'LOCAL'
            ? [Object.freeze({ clientId: row.clientId, file: row.file })]
            : [],
        ),
      ),
    });
  }

  beginLifecycleCompletion(): string {
    if (this.phase !== 'READY' || this.snapshot().dirty) {
      throw new Error(
        'Lifecycle completion requires a clean, ready file editor.',
      );
    }
    if (!this.revisionEtag) {
      throw new Error(
        'Cannot complete lifecycle without a revision validator.',
      );
    }
    this.phase = 'RELOADING';
    return this.revisionEtag;
  }

  completeLifecycleCompletion(revisionEtag: string): void {
    if (this.phase !== 'RELOADING') {
      throw new Error('Revision lifecycle completion is not active.');
    }
    this.revisionEtag = requireValidator(revisionEtag, 'revisionEtag');
  }

  completeNavigation(): boolean {
    if (this.phase !== 'RELOADING') return false;
    this.phase = 'READY';
    return true;
  }

  setCommitPhase(next: CollectionRevisionFileEditorBusyPhase): void {
    if (!this.isBusy()) {
      throw new Error('No file-set commit is active.');
    }
    if (
      COMMIT_PHASE_TRANSITIONS[
        this.phase as CollectionRevisionFileEditorBusyPhase
      ] !== next
    ) {
      throw new Error(
        `Invalid file editor phase transition: ${this.phase} -> ${next}`,
      );
    }
    this.phase = next;
  }

  retainStagedFileIds(fileIds: readonly string[]): void {
    this.stagedFileIds = Object.freeze(
      fileIds.map((fileId) => requireNonEmpty(fileId, 'stagedFileId')),
    );
  }

  failCommit(failure?: CollectionRevisionFileEditorFailure): void {
    if (!this.isBusy()) {
      throw new Error('No file-set commit is active.');
    }
    this.phase = 'ERROR';
    this.failure = failure ? freezeFailure(failure) : null;
  }

  enterConflict(
    conflict: Omit<CollectionRevisionFileEditorConflict, 'editable'>,
  ): void {
    if (!this.isBusy()) {
      throw new Error('No file-set commit is active.');
    }
    this.conflict = Object.freeze({
      current: conflict.current,
      targetEtag: requireValidator(conflict.targetEtag, 'targetEtag'),
      revisionEtag: requireValidator(conflict.revisionEtag, 'revisionEtag'),
      traceId: conflict.traceId,
      editable: isConflictEditable(conflict.current),
    });
    this.phase = 'CONFLICT';
  }

  beginConflictRetry(targetEtag: string, revisionEtag: string): void {
    if (this.phase !== 'CONFLICT' || !this.conflict?.editable) {
      throw new Error('An editable file-set conflict is required for rebase.');
    }
    this.targetEtag = requireValidator(targetEtag, 'targetEtag');
    this.revisionEtag = requireValidator(revisionEtag, 'revisionEtag');
    this.conflict = null;
    this.failure = null;
    this.phase = 'VALIDATING';
  }

  discardConflictAndReload(): boolean {
    const targetFileSet = this.conflict?.current.target_file_set;
    if (this.phase !== 'CONFLICT' || !this.conflict || !targetFileSet) {
      return false;
    }
    const conflict = this.conflict;
    this.applyHydration(
      {
        targetFileSet,
        targetEtag: conflict.targetEtag,
        revisionEtag: conflict.revisionEtag,
      },
      true,
    );
    return true;
  }

  private canMutate(): boolean {
    return this.editable && (this.phase === 'READY' || this.phase === 'ERROR');
  }

  private isBusy(): boolean {
    return BUSY_PHASES.has(this.phase);
  }

  private canCommitDesiredFileSet(): boolean {
    return this.rows.length >= this.requiredFilesCount;
  }

  private isPrimaryActionEnabled(
    summary: CollectionRevisionFileEditorDirtySummary,
  ): boolean {
    if (this.isBusy()) return false;
    const dirty = summary.added > 0 || summary.removed > 0;
    if (this.mode === 'INITIAL_SUBMISSION') {
      return this.editable && dirty && this.canCommitDesiredFileSet();
    }
    return !dirty || (this.editable && this.canCommitDesiredFileSet());
  }

  private dirtySummary(): CollectionRevisionFileEditorDirtySummary {
    const desiredPersistedIds = new Set(
      this.rows.flatMap((row) =>
        row.origin === 'PERSISTED' ? [row.fileId] : [],
      ),
    );
    return Object.freeze({
      added: this.rows.filter((row) => row.origin === 'LOCAL').length,
      removed: this.baseFileIds.filter((id) => !desiredPersistedIds.has(id))
        .length,
      addedFileNames: Object.freeze(
        this.rows.flatMap((row) => (row.origin === 'LOCAL' ? [row.name] : [])),
      ),
      removedFileNames: Object.freeze(
        this.baseRows.flatMap((row) =>
          desiredPersistedIds.has(row.fileId) ? [] : [row.name || row.fileId],
        ),
      ),
    });
  }
}

function isConflictEditable(current: CollectionRevisionCurrentDto): boolean {
  const target = current.target_file_set;
  return Boolean(
    target &&
      (target.revision_status === 'DRAFT' ||
        target.revision_status === 'SUBMITTED') &&
      target.editable &&
      target.allowed_actions.includes('COMMIT_FILE_SET'),
  );
}

function freezeFailure(
  failure: CollectionRevisionFileEditorFailure,
): CollectionRevisionFileEditorFailure {
  return Object.freeze({
    kind: failure.kind,
    message: failure.message,
    fileNames: Object.freeze([...failure.fileNames]),
    cause: failure.cause,
  });
}

function toPersistedRow(
  file: CollectionRevisionTargetFileDto,
): CollectionRevisionPersistedFileRow {
  const fileId = requireNonEmpty(file.file_id, 'file_id');
  return Object.freeze({
    key: `persisted:${fileId}`,
    origin: 'PERSISTED',
    fileId,
    name: file.name,
    mediaType: file.media_type,
    sizeBytes: file.size_bytes,
    validationStatus: file.validation_status,
  });
}

/**
 * Freezes one wire validation file DTO into the editor's snapshot shape,
 * parsing every `required_inputs` entry through `parseCollectionRequiredInput`
 * (D-05). When the response is a statement-origin rejection (D-09) — usable
 * is false, `required_inputs` is empty, and `error_code` is one of the
 * closed `STATEMENT_ORIGIN_*` codes — and the file previously carried a
 * supported `statement_origin_id` descriptor, that descriptor is retained
 * instead of the empty array, so the review queue keeps treating the file as
 * still requiring resolution. Without a prior descriptor, the response falls
 * through to the ordinary permanent-rejection path (empty `required_inputs`).
 */
function freezeValidation(
  file: CollectionRevisionValidationFileDto,
  previousDescriptors: readonly CollectionRequiredInputDescriptor[] = [],
): CollectionRevisionFileValidationSnapshot {
  const parsedRequiredInputs: readonly CollectionRequiredInputDescriptor[] =
    Object.freeze(
      file.required_inputs.map((input) => parseCollectionRequiredInput(input)),
    );
  const retainedOrigins = shouldRetainRejectedStatementOrigin(file)
    ? Object.freeze(previousDescriptors.filter(isStatementOriginRequiredInput))
    : Object.freeze([]);
  return Object.freeze({
    ...file,
    resolved_inputs: freezeResolvedInputs(file.resolved_inputs),
    required_inputs:
      retainedOrigins.length > 0 ? retainedOrigins : parsedRequiredInputs,
  });
}

function shouldRetainRejectedStatementOrigin(
  file: CollectionRevisionValidationFileDto,
): boolean {
  return (
    !file.usable &&
    file.required_inputs.length === 0 &&
    isStatementOriginValidationErrorCode(file.error_code)
  );
}

function freezeResolvedInputs(
  inputs: Readonly<Record<string, string>>,
): Readonly<Record<string, string>> {
  return Object.freeze(
    Object.fromEntries(
      Object.entries(inputs).map(([key, value]) => [
        requireNonEmpty(key, 'resolved input key'),
        requireNonEmpty(value, 'resolved input value'),
      ]),
    ),
  );
}

function withoutKeys<T>(
  source: Readonly<Record<string, T>>,
  removed: ReadonlySet<string>,
): Readonly<Record<string, T>> {
  return Object.freeze(
    Object.fromEntries(
      Object.entries(source).filter(([key]) => !removed.has(key)),
    ),
  );
}

function requireValidator(value: string, field: string): string {
  return requireNonEmpty(value, field);
}

function requireNonEmpty(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}
